/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { OperationVariables, useMutation, useQuery } from '@apollo/client'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import Icon from '@material-ui/core/Icon'
import { UpdateGeneralSetting } from '@services/app/mutation'
import appClient from '@services'
import { remove } from 'local-storage'
import { useUserDispatcher } from '@hooks'
import { GetPointSettings } from '@services/app/query'
import { IServiceResponse } from '@services/model'
import ConfirmationDialog from './localComponents/ConfirmationDialog'

function Alert(props: any) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    width: 350,
    marginLeft: 8,
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}))

const useImperativeQuery = (query: any, client: any) => {
  const { refetch } = useQuery(query, { client, skip: true })

  const imperativelyCallQuery = (variables: Partial<OperationVariables> | undefined) => {
    return refetch(variables)
  }

  return imperativelyCallQuery
}

interface GeneralSetting {
  id: number
  name: string
  value: string | undefined
}

const CoachPoint: React.FC = () => {
  const getPointSettings = useImperativeQuery(GetPointSettings, appClient)
  const { clearUserAuthData } = useUserDispatcher()
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [openSnackBar, setOpenSnackbar] = useState(false)
  const [isGeneralSettingUpdated, setIsGeneralSettingUpdated] = useState(false)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)

  // COACH 
  const [emergingCoachId, setEmergingCoachId] = useState(1)
  const [emergingCoach, setEmergingCoach] = useState<string | undefined>('0')
  const [errorEmergingCoach, setErrorEmergingCoach] = useState(false)

  const [greatCoachId, setGreatCoachId] = useState(1)
  const [greatCoach, setGreatCoach] = useState<string | undefined>('0')
  const [errorGreatCoach, setErrorGreatCoach] = useState(false)

  const [expertCoachId, setExpertCoachId] = useState(1)
  const [expertCoach, setExpertCoach] = useState<string | undefined>('0')
  const [errorExpertCoach, setErrorExpertCoach] = useState(false)

  const [masterCoachId, setMasterCoachId] = useState(1)
  const [masterCoach, setMasterCoach] = useState<string | undefined>('0')
  const [errorMasterCoach, setErrorMasterCoach] = useState(false)

  const [updateGeneralSetting] = useMutation(UpdateGeneralSetting, { client: appClient })

  const goToLogin = () => {
    remove('token')
    clearUserAuthData()
  }

  const handleData = (userPointSettings: GeneralSetting[]) => {
    for (const item of userPointSettings) {
      switch (item.name) {
        case 'EMERGING_COACH':
          setEmergingCoachId(item.id)
          setEmergingCoach(item.value)
          break
        case 'GREAT_COACH':
          setGreatCoachId(item.id)
          setGreatCoach(item.value)
          break
        case 'EXPERT_COACH':
          setExpertCoachId(item.id)
          setExpertCoach(item.value)
          break
        case 'MASTER_COACH':
          setMasterCoachId(item.id)
          setMasterCoach(item.value)
          break
      }
    }
  }

  const retrieveCoachSettings = async () => {
    try {
      const getPointSettingsResp = await getPointSettings(undefined)
      if (getPointSettingsResp != null) {
        const { data: { getAppSettings: { result } } } = getPointSettingsResp
        handleData(result.userPointSettings)
      }
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }
      console.error(e)
    }
  }

  useEffect(() => {
    retrieveCoachSettings()
  }, [])

  const handleSavePointSetting = async () => {
    setOpenConfirmationDialog(false)

    try {
      setIsLoading(true)

      const updateGeneralSettingVariables = {
        settings: [{
          id: emergingCoachId,
          value: emergingCoach
        },
        {
          id: greatCoachId,
          value: greatCoach
        },
        {
          id: expertCoachId,
          value: expertCoach
        },
        {
          id: masterCoachId,
          value: masterCoach
        },
        ]
      }

      const updateGeneralSettingResp = await updateGeneralSetting({ variables: updateGeneralSettingVariables })
      const { result } = updateGeneralSettingResp.data.updateGeneralSetting as IServiceResponse<boolean>
      setIsGeneralSettingUpdated(result)

      setOpenSnackbar(true)
    } catch (e) {
      if (e?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }

      console.error(e)
    }
    setIsLoading(false)
  }

  const handleOnCloseSnackbar = () => {
    setOpenSnackbar(false)
  }
  //Max chars for title and body and other data are 4000bytes, but to prevent issue we limit to 1024 for body
  //https://firebase.google.com/docs/cloud-messaging/concept-options
  return <div style={{
    border: '1px solid #dedede',
    padding: '20px',
    borderRadius: '5px',
    background: '#fff',
    width: '400px'
  }}>
    <h3>Coach Point</h3>
    <Snackbar open={openSnackBar} onClose={handleOnCloseSnackbar} autoHideDuration={6000}>
      <Alert onClose={handleOnCloseSnackbar} severity={isGeneralSettingUpdated ? 'success' : 'error'}>
        {isGeneralSettingUpdated ? 'Coach Point has successfully updated!' : 'Failed to send Coach Point!'}
      </Alert>
    </Snackbar>
    {openConfirmationDialog && <ConfirmationDialog open={openConfirmationDialog} handleYes={handleSavePointSetting} handleNo={() => setOpenConfirmationDialog(false)} />}
    <form className={classes.root} noValidate autoComplete="off">
      <FormControl fullWidth>
        <TextField
          autoFocus
          id="EmergingCoach"
          type="number"
          label="Emerging Coach*"
          value={emergingCoach}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setEmergingCoach(value)
            setErrorEmergingCoach(value === '')
          }}
        />
        {errorEmergingCoach && <span style={{ color: 'red', fontSize: 12 }}>Emerging Coach Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="GreatCoach"
          type="number"
          label="Great Coach*"
          value={greatCoach}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setGreatCoach(value)
            setErrorGreatCoach(value === '')
          }}
        />
        {errorGreatCoach && <span style={{ color: 'red', fontSize: 12 }}>Great Coach Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="ExpertCoach"
          type="number"
          label="Expert Coach*"
          value={expertCoach}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setExpertCoach(value)
            setErrorExpertCoach(value === '')
          }}
        />
        {errorExpertCoach && <span style={{ color: 'red', fontSize: 12 }}>Expert Coach Value is required</span>}
      </FormControl>

      <FormControl fullWidth>
        <TextField
          autoFocus
          id="MasterCoach"
          type="number"
          label="Master Coach*"
          value={masterCoach}
          fullWidth
          inputProps={{ maxLength: 50, shrink: true, }}
          disabled={isLoading}
          onChange={e => {
            const value = e.currentTarget.value
            setMasterCoach(value)
            setErrorMasterCoach(value === '')
          }}
        />
        {errorMasterCoach && <span style={{ color: 'red', fontSize: 12 }}>Master Coach Value is required</span>}
      </FormControl>

      <FormControl>
        <Button disabled={isLoading} variant="contained" startIcon={<Icon>save</Icon>} onClick={() => {
          const isEmergingCoachEmpty = emergingCoach === ''
          setErrorEmergingCoach(isEmergingCoachEmpty)

          const isGreatCoachEmpty = greatCoach === ''
          setErrorGreatCoach(isGreatCoachEmpty)

          const isExpertCoachEmpty = expertCoach === ''
          setErrorExpertCoach(isExpertCoachEmpty)

          const isMasterCoachEmpty = masterCoach === ''
          setErrorExpertCoach(isMasterCoachEmpty)

          if (isEmergingCoachEmpty || isGreatCoachEmpty || isExpertCoachEmpty || isMasterCoachEmpty) {
            return
          }

          const isEmergingCoachInvalid = isNaN(parseFloat(emergingCoach))
          setErrorEmergingCoach(isEmergingCoachInvalid)

          const isGreatCoachInvalid = isNaN(parseFloat(greatCoach))
          setErrorGreatCoach(isGreatCoachInvalid)

          const isExpertCoachInvalid = isNaN(parseFloat(expertCoach))
          setErrorExpertCoach(isGreatCoachInvalid)

          const isMasterCoachInvalid = isNaN(parseFloat(masterCoach))
          setErrorExpertCoach(isMasterCoachInvalid)

          if (isEmergingCoachInvalid || isGreatCoachInvalid || isExpertCoachInvalid || isMasterCoachInvalid) {
            return
          }

          setOpenConfirmationDialog(true)
        }} color="primary">
          Update
        </Button>
      </FormControl>
    </form >
  </div >
}

export default CoachPoint